import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CourseDashboard from './components/CourseDashboard';
import CourseView from './components/CourseView';
import CoursePractice from './components/CoursePractice';
import CourseChatView from './components/CourseChatView';
import NewStudySession from './components/study-session/NewStudySession';
import CreateSession from './components/CreateSession';
import QuickCreateStudySession from './components/quick-actions/QuickCreateStudySession';
import ExamListView from './components/ExamListView';
import ExamCreationView from './components/ExamCreationView';
import ExamAIGenerationView from './components/ExamAIGenerationView';
import ExamPracticeView from './components/ExamPracticeView';
import ExamStudyGuideView from './components/ExamStudyGuideView';
import ExamPdfExportView from './components/ExamPdfExportView';
import StudySessionPdfExportView from './components/StudySessionPdfExportView';
import CourseMaterialsView from './components/CourseMaterialsView';
import SharedExamView from './components/share/SharedExamView';
import SharedStudyMaterialsView from './components/share/SharedStudyMaterialsView';
import Login from './components/Login';
import Register from './components/Register';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import EmailVerification from './components/EmailVerification';
import OnboardingPage from './components/OnboardingPage';
import Profile from './components/Profile';
import AdminDashboard from './components/AdminDashboard';
import ProtectedRoute from './components/ProtectedRoute';
import SpaceThemeDemo from './components/SpaceThemeDemo';
import MathTestPage from './components/MathTestPage';
import TimelineTestPage from './components/TimelineTestPage';
import GraphTestPage from './components/GraphTestPage';
import ErrorMessage from './components/ui/ErrorMessage';
import Navbar from './components/layout/Navbar';
import { ThemeProvider, useTheme } from './components/layout/ThemeProvider';
import SpaceBackground from './components/layout/SpaceBackground';
import { FullScreenLoading } from './components/ui/LoadingSpinner';
import { isAuthenticated, removeAuthToken, authApi } from './services/api';
import { analytics } from './services/analytics';
import { CreditErrorProvider } from './components/credits/CreditErrorProvider';
import { useCreditErrorInterceptor } from './hooks/useCreditErrorInterceptor';
import './i18n'; // Initialize i18n
import { getTextDirection } from './i18n';

// Conditional Space Background Component
const ConditionalSpaceBackground: React.FC<{ theme: 'light' | 'dark' | 'mixed' }> = ({ theme }) => {
  const location = useLocation();
  
  // Routes where we want to disable the background stars for better focus
  const focusRoutes = [
    '/session/', // Study sessions
    '/course/', // Course pages that might include exam practice
  ];
  
  // Check if current path contains exam practice
  const isExamPractice = location.pathname.includes('/practice');
  
  // Check if current path is a focus route  
  const isFocusRoute = focusRoutes.some(route => location.pathname.includes(route));
  
  // Disable background for study sessions and exam practice
  if (isFocusRoute || isExamPractice) {
    return (
      <SpaceBackground 
        theme={theme} 
        intensity="none"
      />
    );
  }
  
  // Default background for other pages
  return (
    <SpaceBackground 
      theme={theme} 
      intensity={theme === 'dark' ? 'cosmic' : 'normal'}
    />
  );
};

// Main App Content Component (needs to be inside ThemeProvider)
const AppContent: React.FC = () => {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const { t, i18n } = useTranslation();
  const { theme } = useTheme();
  const location = useLocation();
  const isRTL = i18n.language === 'he';
  
  // Add refs to prevent duplicate API calls
  const initAuthInProgressRef = useRef(false);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Initialize credit error interceptor
  useCreditErrorInterceptor();
  
  // RTL support - update document direction when language changes
  useEffect(() => {
    const direction = getTextDirection(i18n.language);
    document.documentElement.dir = direction;
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  const initAuth = async (updateLoading: boolean = true) => {
    // Prevent duplicate calls
    if (initAuthInProgressRef.current) {
      return;
    }
    
    initAuthInProgressRef.current = true;
    
    try {
      if (isAuthenticated()) {
        try {
          const userData = await authApi.getCurrentUser();
          setUser(userData);
          
          // Identify user in analytics
          analytics.identify(userData.id.toString(), {
            email: userData.email,
            signup_date: userData.created_at,
          });
        } catch (error) {
          // Token might be invalid, clear it
          removeAuthToken();
          setUser(null);
        }
      } else {
        setUser(null);
      }
      if (updateLoading) {
        setLoading(false);
      }
    } finally {
      initAuthInProgressRef.current = false;
    }
  };

  const debouncedInitAuth = (updateLoading: boolean = false) => {
    // Clear existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    // Set new timeout
    debounceTimeoutRef.current = setTimeout(() => {
      initAuth(updateLoading);
    }, 100); // 100ms debounce
  };

  useEffect(() => {
    // Initialize PostHog analytics
    analytics.init();
    initAuth();
  }, []);

  // Listen for storage changes (when user logs in from another tab or after login)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      // Only react to token changes to prevent unnecessary calls
      if (e.key === 'token' || e.key === null) {
        debouncedInitAuth(false); // Don't update loading state when called from event
      }
    };

    const handleUserLoggedIn = () => {
      debouncedInitAuth(false);
    };

    // Listen for login events
    window.addEventListener('storage', handleStorageChange);
    
    // Custom event for same-tab login
    window.addEventListener('userLoggedIn', handleUserLoggedIn);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userLoggedIn', handleUserLoggedIn);
      
      // Clear timeout on cleanup
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const handleLogout = () => {
    analytics.trackUserLoggedOut();
    analytics.reset(); // Clear PostHog user session
    removeAuthToken();
    setUser(null);
    window.location.href = '/login';
  };

  if (loading) {
    return (
      <FullScreenLoading 
        message=""
        variant="astronaut"
        emoji="🚀"
      />
    );
  }

  const isShareRoute = location.pathname.startsWith('/share/');
  const showNavbar = isAuthenticated() || isShareRoute;
  const guestMode = isShareRoute && !isAuthenticated();

  return (
    <div className="min-h-screen relative flex flex-col">
        {/* Conditional Space Background */}
        <ConditionalSpaceBackground theme={theme} />
        
        {/* Navbar */}
        {showNavbar && (
          <Navbar user={user} onLogout={handleLogout} guestMode={guestMode} />
        )}

        {/* Main Content Container */}
        <main className="flex-1 relative z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">
            <Routes>
            {/* Public routes */}
            <Route 
              path="/share/study/:token" 
              element={<SharedStudyMaterialsView />} 
            />
            <Route 
              path="/share/exam/:token" 
              element={<SharedExamView />} 
            />
              <Route 
                path="/login" 
                element={
                  isAuthenticated() ? <Navigate to="/" replace /> : <Login />
                } 
              />
              <Route 
                path="/register" 
                element={
                  isAuthenticated() ? <Navigate to="/" replace /> : <Register />
                } 
              />
              <Route 
                path="/forgot-password" 
                element={
                  isAuthenticated() ? <Navigate to="/" replace /> : <ForgotPassword />
                } 
              />
              <Route 
                path="/reset-password" 
                element={
                  isAuthenticated() ? <Navigate to="/" replace /> : <ResetPassword />
                } 
              />
              <Route 
                path="/verify-email" 
                element={
                  isAuthenticated() ? <Navigate to="/" replace /> : <EmailVerification />
                } 
              />
              
              {/* Protected routes */}
              <Route 
                path="/" 
                element={
                  <ProtectedRoute>
                    <CourseDashboard />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/onboarding" 
                element={
                  <ProtectedRoute>
                    <OnboardingPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/course/:id" 
                element={
                  <ProtectedRoute>
                    <CourseView />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/course/:courseId/practice" 
                element={
                  <ProtectedRoute>
                    <CoursePractice />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/course/:courseId/chat" 
                element={
                  <ProtectedRoute>
                    <CourseChatView />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/create-session" 
                element={
                  <ProtectedRoute>
                    <CreateSession />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/session/:id" 
                element={
                  <ProtectedRoute>
                    <NewStudySession />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/study-session/:sessionId/pdf-export" 
                element={
                  <ProtectedRoute>
                    <StudySessionPdfExportView />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/profile" 
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin" 
                element={
                  <ProtectedRoute>
                    <AdminDashboard />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/course/:courseId/exams" 
                element={
                  <ProtectedRoute>
                    <ExamListView />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/course/:courseId/exams/create" 
                element={
                  <ProtectedRoute>
                    <ExamCreationView />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/course/:courseId/exams/ai-generate" 
                element={
                  <ProtectedRoute>
                    <ExamAIGenerationView />
                  </ProtectedRoute>
                } 
              />
              {/* Quick Action Routes */}
              <Route 
                path="/quick/create-study-session" 
                element={
                  <ProtectedRoute>
                    <QuickCreateStudySession />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/exams/create" 
                element={
                  <ProtectedRoute>
                    <ExamCreationView />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/exams/ai-generate" 
                element={
                  <ProtectedRoute>
                    <ExamAIGenerationView />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/course/:courseId/exams/:examId/practice" 
                element={
                  <ProtectedRoute>
                    <ExamPracticeView />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/course/:courseId/exam/:examId/study-guide" 
                element={
                  <ProtectedRoute>
                    <ExamStudyGuideView />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/course/:courseId/study-guide" 
                element={
                  <ProtectedRoute>
                    <ExamStudyGuideView />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/course/:courseId/materials" 
                element={
                  <ProtectedRoute>
                    <CourseMaterialsView />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/course/:courseId/exam/:examId/pdf-export" 
                element={
                  <ProtectedRoute>
                    <ExamPdfExportView />
                  </ProtectedRoute>
                } 
              />
              
              {/* Dev-only routes */}
              {process.env.NODE_ENV === 'development' && (
                <>
                  {/* Space Theme Demo - dev only */}
                  <Route 
                    path="/space-demo" 
                    element={<SpaceThemeDemo />} 
                  />
                  
                  {/* Math Test Page - dev only */}
                  <Route 
                    path="/math-test" 
                    element={<MathTestPage />} 
                  />

                  {/* Graph Test Page - dev only */}
                  <Route
                    path="/graph-test"
                    element={<GraphTestPage />}
                  />
                  
                  {/* Error Message Test - dev only */}
                  <Route 
                    path="/error-test" 
                    element={<ErrorMessage />} 
                  />
                </>
              )}
              
              {/* Test route for timeline graphs */}
              <Route path="/timeline-test" element={<TimelineTestPage />} />
              
              {/* Redirect to login if no route matches and not authenticated */}
              <Route 
                path="*" 
                element={
                  isAuthenticated() ? <Navigate to="/" replace /> : <Navigate to="/login" replace />
                } 
              />
            </Routes>
          </div>
        </main>

    </div>
  );
};

// Main App Component with ThemeProvider
function App() {
  return (
    <ThemeProvider>
      <CreditErrorProvider>
        <Router>
          <AppContent />
        </Router>
      </CreditErrorProvider>
    </ThemeProvider>
  );
}

export default App;
